import { Filter } from '@cubejs-client/core';
import { QueryErrorMessages } from '../../enum/cube-error-message.enum';
import { Weekdays } from '../../enum/cube-schema.enum';
import { ICubeRepository } from '../../interface/cube-repository.interface';
import {
  GetTeamHeatmapProps,
  GetUserHeatmapProps,
  IHeatmapRepository,
} from './heatmap-repository.interface';
import { IWorkingDaysAnalyticsActivityV2Enum } from '../../enum/working-days-analytics-activity.enum';
import {
  ITeamAnalyticsActivityEnum,
  ITeamAnalyticsActivityV2Enum,
} from '../../enum/team-analytics-activity/team-analytics-activity.enum';
import {
  HeatmapDomainEntity,
  HeatmapReforgeMapper,
  HeatmapPersistenceRecord,
} from '../../mappers/heatmap/heatmap.mapper';

export class HeatmapRepository implements IHeatmapRepository {
  cubeRepository: ICubeRepository;
  constructor(deps: { repository: ICubeRepository }) {
    this.cubeRepository = deps.repository;
  }

  async getUserHeatmap(
    props: GetUserHeatmapProps
  ): Promise<HeatmapDomainEntity[]> {
    try {
      const filters: Filter[] = [];
      if (
        props.params.segment &&
        Object.values<string>(Weekdays).includes(props.params.segment)
      ) {
        const index = String(
          Object.values<string>(Weekdays).indexOf(props.params.segment)
        );
        filters.push({
          member: IWorkingDaysAnalyticsActivityV2Enum.WeekdayIndex,
          operator: 'equals',
          values: [index],
        });
      }

      filters.push({
        member: IWorkingDaysAnalyticsActivityV2Enum.AppId,
        operator: 'equals',
        values: [props.params.appId],
      });

      filters.push({
        member: IWorkingDaysAnalyticsActivityV2Enum.LocalStartTime,
        operator: 'inDateRange',
        values: [props.params.startDate, props.params.endDate],
      });
      const result: HeatmapPersistenceRecord[] =
        await this.cubeRepository.query(props.config.token, {
          dimensions: [
            IWorkingDaysAnalyticsActivityV2Enum.HeatmapSegment,
            IWorkingDaysAnalyticsActivityV2Enum.UserId,
          ],
          measures: [
            IWorkingDaysAnalyticsActivityV2Enum.TotalDuration,
            IWorkingDaysAnalyticsActivityV2Enum.count,
          ],
          filters,
        });
      return result.map((record) =>
        HeatmapReforgeMapper.persistenceToDomain(record)
      );
    } catch (error) {
      throw new Error(`${QueryErrorMessages.QueryHeatMapsError}: ${error}`);
    }
  }

  async getWorkspaceHeatmap(
    props: GetTeamHeatmapProps
  ): Promise<HeatmapDomainEntity[]> {
    try {
      const filters: Filter[] = [];
      if (
        props.params.segment &&
        Object.values<string>(Weekdays).includes(props.params.segment)
      ) {
        const index = String(
          Object.values<string>(Weekdays).indexOf(props.params.segment)
        );
        filters.push({
          member: ITeamAnalyticsActivityEnum.WeekdayIndex,
          operator: 'equals',
          values: [index],
        });
      }

      filters.push({
        member: ITeamAnalyticsActivityEnum.AppId,
        operator: 'equals',
        values: [props.params.appId],
      });

      filters.push({
        member: ITeamAnalyticsActivityEnum.LocalStartTime,
        operator: 'inDateRange',
        values: [props.params.startDate, props.params.endDate],
      });
      filters.push({
        member: ITeamAnalyticsActivityEnum.TeamId,
        operator: 'equals',
        values: [props.params.teamId],
      });

      filters.push({
        member: ITeamAnalyticsActivityEnum.UserId,
        operator: 'equals',
        values: props.params.userIds,
      });

      const result = await this.cubeRepository.query(props.config.token, {
        dimensions: [
          ITeamAnalyticsActivityEnum.HeatmapSegment,
          ITeamAnalyticsActivityEnum.UserId,
        ],
        measures: [
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.Count,
        ],
        filters,
      });
      return result.map((record) =>
        HeatmapReforgeMapper.persistenceToDomain(record)
      );
    } catch (error) {
      throw new Error(`${QueryErrorMessages.QueryHeatMapsError}: ${error}`);
    }
  }

  async getWorkspaceHeatmapV2(
    props: GetTeamHeatmapProps
  ): Promise<HeatmapDomainEntity[]> {
    try {
      const filters: Filter[] = [];
      if (
        props.params.segment &&
        Object.values<string>(Weekdays).includes(props.params.segment)
      ) {
        const index = String(
          Object.values<string>(Weekdays).indexOf(props.params.segment)
        );
        filters.push({
          member: ITeamAnalyticsActivityV2Enum.WeekdayIndex,
          operator: 'equals',
          values: [index],
        });
      }

      filters.push({
        member: ITeamAnalyticsActivityV2Enum.AppId,
        operator: 'equals',
        values: [props.params.appId],
      });

      filters.push({
        member: ITeamAnalyticsActivityV2Enum.LocalStartTime,
        operator: 'inDateRange',
        values: [props.params.startDate, props.params.endDate],
      });
      filters.push({
        member: ITeamAnalyticsActivityV2Enum.TeamId,
        operator: 'equals',
        values: [props.params.teamId],
      });

      filters.push({
        member: ITeamAnalyticsActivityV2Enum.UserId,
        operator: 'equals',
        values: props.params.userIds,
      });

      const result = await this.cubeRepository.query(props.config.token, {
        dimensions: [
          ITeamAnalyticsActivityV2Enum.HeatmapSegment,
          ITeamAnalyticsActivityV2Enum.UserId,
        ],
        measures: [
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.Count,
        ],
        filters,
      });
      return result.map((record) =>
        HeatmapReforgeMapper.persistenceToDomainV2(record)
      );
    } catch (error) {
      throw new Error(`${QueryErrorMessages.QueryHeatMapsError}: ${error}`);
    }
  }
}

